import { isFlagEnabled } from './featureFlagUtils';

/**
 * Verifica se o perfil do usuário tem todos os campos obrigatórios preenchidos.
 * Espelha a lógica de isFormValid() em FirstAccess/index.js, mas lendo o objeto
 * user já carregado em vez de formData.
 *
 * Usado pelo FirstAccessGuard quando CONFIGS.VERIFY_INCOMPLETE_PROFILE está ativo
 * para capturar usuários SSO com dados incompletos.
 */
/**
 * Verifica se o backend/CMS marcou explicitamente o usuário como precisando
 * atualizar o perfil, via CONFIGS.INCOMPLETE_PROFILE_FIELD (nome de atributo
 * configurável por tenant, ex.: "requiresProfileUpdate").
 *
 * Funciona como sinal OU com isProfileComplete(): força o usuário a passar
 * pelo First Access mesmo que os campos já pareçam completos (ex.: migração
 * de dados, reconfirmação exigida pelo backend).
 */
export const isIncompleteProfileFlagSet = (user, config) => {
  const fieldName = config?.CONFIGS?.INCOMPLETE_PROFILE_FIELD;
  if (!fieldName) return false;
  const value = user?.attributes?.[fieldName];
  return value === true || value === 'true';
};

export const isProfileComplete = (user, config) => {
  if (!user) return false;

  // Com DISABLE_DOCUMENT_MASK o "documento" pode não ser CPF/CNPJ (letras,
  // outro tamanho) — não faz sentido nem remover não-dígitos, nem inferir
  // PJ/PF pelo tamanho (ver isDocumentMaskDisabled() em useTenant.js).
  const documentMaskDisabled = config?.CONFIGS?.DISABLE_DOCUMENT_MASK ?? false;
  const doc = documentMaskDisabled
    ? String(user.document || '').trim()
    : String(user.document || '').replace(/\D/g, '');
  if (!doc) return false;

  const isPJ = documentMaskDisabled ? false : doc.length === 14;
  const hideBirthDate = config?.CONFIGS?.HIDE_BIRTH_DATE ?? false;
  const nameDisabled = config?.CONFIGS?.DISABLE_NAME ?? false;

  if (isPJ) {
    if (!user.name?.trim()) return false;
    if (!user.attributes?.legal_representative_name?.trim()) return false;
    if (!user.attributes?.legal_representative_cpf?.trim()) return false;
  } else {
    if (!nameDisabled && !user.name?.trim()) return false;
    if (!hideBirthDate && !user.birthDate) return false;
  }

  const hasEmail = user.emails?.some((e) => e.emailAddress?.trim());
  if (!hasEmail) return false;

  const primaryPhone = user.phones?.[0];
  const hasPhone = primaryPhone?.ddd && primaryPhone?.phoneNumber;
  if (!hasPhone) return false;

  // Endereço só é exigido quando o tenant exibe a seção no formulário
  // (FEATURE_FLAGS.ADDRESS_SECTION) — ver isFormValid() em FirstAccess/index.js.
  // Sem esse gate, um tenant sem a seção habilitada travava o usuário num loop:
  // o guard exigia o endereço, mas o formulário nunca mostrava o campo pra preencher.
  if (isFlagEnabled(config?.FEATURE_FLAGS?.ADDRESS_SECTION)) {
    const addr = user.addresses?.[0];
    if (
      !addr?.zipCode?.trim() ||
      !addr?.street?.trim() ||
      !addr?.number?.trim() ||
      !addr?.neighborhood?.trim() ||
      !addr?.city?.trim() ||
      !addr?.state?.trim()
    ) {
      return false;
    }
  }

  // Campos extras com required:true definidos pelo tenant
  const docType = isPJ ? 'cnpj' : 'cpf';
  const requiredExtras = (config?.EXTRA_FIELDS ?? []).filter((field) => {
    if (!field.required) return false;
    if (!field.conditions?.documentType) return true;
    return field.conditions.documentType.includes(docType);
  });
  for (const field of requiredExtras) {
    const value = user.attributes?.[field.id];
    if (field.fieldType === 'checkbox') {
      if (value !== 'true') return false;
    } else if (!value?.trim()) {
      return false;
    }
  }

  // Profile select obrigatório
  // Fallback em attributes.profile: a API às vezes não persiste/retorna o campo
  // `profile` de nível raiz (ver PUT /api/Participants), então gravamos o mesmo
  // valor também em attributes.profile e lemos com fallback aqui.
  const profileSelect = config?.PROFILE_SELECT;
  if (profileSelect?.enabled && profileSelect?.required && !(user.profile || user.attributes?.profile)) return false;

  return true;
};
